import {
	Children,
	type FC,
	Fragment,
	isValidElement,
	type ReactNode,
	useMemo,
	useSyncExternalStore
} from 'react';
import { Route, Router, Routes, useLocation } from 'react-router-dom';
import { RouteRegistry } from '../routes/resolve';
import type {
	AppId,
	ExternalNavigationOptions,
	ExternalTab,
	NavigationGuard,
	NavigationLifecycleEvent,
	NavigationRequest,
	QueryParams,
	ResolvedRoute,
	RouteContentProps,
	RouteElementProps,
	RouteParams,
	RouterProps,
	RoutingClientBootstrapConfig,
	RoutingClientLifecycleConfig
} from '../routes/types';
import { NavigationIntent, OrganizationType } from '../routes/types';
import { createRouteAccessGate } from './RouteAccessGate';

type RouterLocation = {
	hash: string;
	key: string;
	pathname: string;
	search: string;
	state: unknown;
};

type RouterTo =
	| string
	| {
			hash?: string;
			pathname?: string;
			search?: string;
	  };

const ROUTING_CLIENT_LOCATION_CHANGE_EVENT = 'routing-client:location-change';

const getRouterLocation = (): RouterLocation => ({
	hash: globalThis.location?.hash ?? '',
	key: 'default',
	pathname: globalThis.location?.pathname ?? '/',
	search: globalThis.location?.search ?? '',
	state: globalThis.history?.state ?? null
});

const createRouterHref = (to: RouterTo): string => {
	if (typeof to === 'string') {
		return to;
	}

	return `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
};

class RoutingClient {
	#baseRegistry: RouteRegistry;
	#registry: RouteRegistry;
	#registeredAppIds: readonly AppId[] = [];
	#externalAppOrigins: Partial<Record<AppId, string>> = {};
	#currentRoute: ResolvedRoute | null = null;
	#routerLocation: RouterLocation = getRouterLocation();
	#isBootstrapped = false;
	#onAfterNavigate: RoutingClientLifecycleConfig['onAfterNavigate'];
	#onBeforeNavigate: RoutingClientLifecycleConfig['onBeforeNavigate'];
	#guards: NavigationGuard[] = [];

	constructor(registry = new RouteRegistry()) {
		this.#baseRegistry = registry;
		this.#registry = registry;
	}

	bootstrap(config: RoutingClientBootstrapConfig): void {
		const { apps, authCallbackOverrideUrl, externalAppOrigins } = config;
		this.#registry = this.#baseRegistry;

		if (authCallbackOverrideUrl) {
			this.#registry = this.#registry.override(NavigationIntent.AuthCallback, {
				buildPath: () => authCallbackOverrideUrl
			});
		}

		this.#registeredAppIds = this.#getRegisteredAppIds(apps);
		this.#externalAppOrigins = externalAppOrigins ?? {};
		this.#isBootstrapped = true;
		this.#routerLocation = getRouterLocation();
		this.#currentRoute = null;
		this.#currentRoute = this.#resolveCurrentRoute();
	}

	getAuthCallbackPath(): string {
		return (
			this.#registry.getRoute(NavigationIntent.AuthCallback)?.buildPath?.({}) ??
			'/auth/login/callback'
		);
	}

	getAuthLoginPath(): string {
		return (
			this.#registry.getRoute(NavigationIntent.AuthLogin)?.buildPath?.({}) ??
			'/auth/login'
		);
	}

	matchUrl(url: string): ResolvedRoute | null {
		const parsedUrl = this.#parseSafeUrl(url);

		if (!parsedUrl) {
			return null;
		}

		return this.#registry.match(`${parsedUrl.pathname}${parsedUrl.search}`);
	}

	matchScopedUrl(url: string): {
		organizationType: OrganizationType;
		organizationIdn: string;
		returnTo: string;
	} | null {
		const parsedUrl = this.#parseSafeUrl(url);

		if (!parsedUrl) {
			return null;
		}

		const relativeUrl = `${parsedUrl.pathname}${parsedUrl.search}`;
		const resolved = this.#registry.match(relativeUrl);

		if (resolved?.params.organizationType && resolved.params.organizationIdn) {
			return {
				organizationType: resolved.params.organizationType,
				organizationIdn: resolved.params.organizationIdn,
				returnTo: relativeUrl
			};
		}

		const scopedPrefix = this.#matchScopedPrefix(parsedUrl);

		if (!scopedPrefix) {
			return null;
		}

		return {
			organizationType: scopedPrefix.organizationType,
			organizationIdn: scopedPrefix.organizationIdn,
			returnTo: relativeUrl
		};
	}

	configureLifecycle(config: RoutingClientLifecycleConfig): void {
		this.#onAfterNavigate = config.onAfterNavigate;
		this.#onBeforeNavigate = config.onBeforeNavigate;
	}

	registerNavigationGuard(guard: NavigationGuard): () => void {
		this.#guards.push(guard);
		return () => {
			this.#guards = this.#guards.filter((g) => g !== guard);
		};
	}

	#isNavigationBlocked(event: NavigationLifecycleEvent): boolean {
		return this.#guards.some((guard) => guard(event) === true);
	}

	#getRegisteredAppIds(
		apps: RoutingClientBootstrapConfig['apps']
	): readonly AppId[] {
		if (apps.length === 0) {
			throw new Error('At least one app must be registered for routing');
		}

		const appIds = apps.map((app) => app.appId);

		if (new Set(appIds).size !== appIds.length) {
			throw new Error('App can be registered for routing only once');
		}

		const hostApps = apps.filter((app) => app.isHost);

		if (hostApps.length > 1) {
			throw new Error('Only one host app can be registered for routing');
		}

		if (apps.length > 1 && hostApps.length === 0) {
			throw new Error('Multiple apps require one host app for routing');
		}

		return appIds;
	}

	canNavigate(request: NavigationRequest): boolean {
		this.#assertBootstrapped();

		if (!this.#registry.getRoute(request.intent)?.buildPath) {
			return false;
		}

		const route = this.#registry.resolve(this.#withCurrentRouteParams(request));

		return this.#isRouteReachable(route);
	}

	resolveNavigation(request: NavigationRequest): ResolvedRoute {
		this.#assertBootstrapped();

		return this.#registry.resolve(this.#withCurrentRouteParams(request));
	}

	emitNavigation(request: NavigationRequest): ResolvedRoute {
		this.#assertBootstrapped();

		const previousRoute = this.#resolveCurrentRoute();
		const normalizedRequest = this.#withCurrentRouteParams(request);
		const targetRoute = this.#registry.resolve(normalizedRequest);

		const lifecycleEvent = { currentRoute: previousRoute, targetRoute };

		this.#onBeforeNavigate?.(lifecycleEvent);

		if (this.#isNavigationBlocked(lifecycleEvent)) {
			return targetRoute;
		}

		if (!this.#isAppAvailable(targetRoute.appId)) {
			const externalUrl = this.#resolveExternalUrl(targetRoute);

			if (!externalUrl) {
				throw new Error(`App is not available for route: ${targetRoute.appId}`);
			}

			this.emitExternalNavigation(externalUrl, {
				replace: normalizedRequest.replace
			});

			this.#onAfterNavigate?.({
				currentRoute: previousRoute,
				targetRoute
			});

			return targetRoute;
		}

		this.#navigate(targetRoute.url, normalizedRequest);
		this.#currentRoute = targetRoute;

		this.#onAfterNavigate?.({
			currentRoute: previousRoute,
			targetRoute
		});

		return targetRoute;
	}

	emitNavigationToUrl(
		url: string,
		options?: { replace?: boolean }
	): ResolvedRoute {
		this.#assertBootstrapped();

		const targetRoute = this.#registry.match(url);

		if (!targetRoute) {
			throw new Error(`Unknown navigation URL: ${url}`);
		}

		if (!this.#isAppAvailable(targetRoute.appId)) {
			throw new Error(`App is not available for route: ${targetRoute.appId}`);
		}

		const previousRoute = this.#resolveCurrentRoute();
		const lifecycleEvent = { currentRoute: previousRoute, targetRoute };

		this.#onBeforeNavigate?.(lifecycleEvent);

		if (this.#isNavigationBlocked(lifecycleEvent)) {
			return targetRoute;
		}

		this.#navigate(targetRoute.url, {
			intent: targetRoute.intent,
			params: targetRoute.params,
			query: targetRoute.query,
			replace: options?.replace
		});
		this.#currentRoute = targetRoute;

		this.#onAfterNavigate?.({
			currentRoute: previousRoute,
			targetRoute
		});

		return targetRoute;
	}

	goBack(fallback: NavigationRequest): void {
		this.#assertBootstrapped();

		if (!this.#canGoBack()) {
			this.emitNavigation(fallback);
			return;
		}

		const currentRoute = this.#resolveCurrentRoute();
		const lifecycleEvent = {
			currentRoute,
			targetRoute: this.#registry.resolve(
				this.#withCurrentRouteParams(fallback)
			)
		};

		this.#onBeforeNavigate?.(lifecycleEvent);

		if (this.#isNavigationBlocked(lifecycleEvent)) {
			return;
		}

		globalThis.history?.back();

		this.#onAfterNavigate?.(lifecycleEvent);
	}

	#canGoBack(): boolean {
		return (globalThis.history?.length ?? 0) > 1;
	}

	getCurrentRoute(): ResolvedRoute | null {
		this.#assertBootstrapped();

		return this.#resolveCurrentRoute();
	}

	getRouteParams(): RouteParams {
		return this.getCurrentRoute()?.params ?? {};
	}

	getQueryParams(): QueryParams {
		return this.getCurrentRoute()?.query ?? {};
	}

	getQueryParam(key: string): string | null {
		const value = this.getCurrentRoute()?.query[key];

		if (value == null) {
			return null;
		}

		if (Array.isArray(value)) {
			return value.length > 0 ? String(value[0]) : null;
		}

		return String(value);
	}

	getCurrentUrl(): string {
		return globalThis.location?.href ?? '';
	}

	getCurrentUrlWithParam(key: string, value: string): string {
		const currentUrl = this.getCurrentUrl();

		try {
			const url = new URL(currentUrl);

			if (!url.searchParams.has(key)) {
				url.searchParams.set(key, value);
				return url.toString();
			}
		} catch {
			// malformed URL — return as-is
		}

		return currentUrl;
	}

	getOrigin(): string {
		return globalThis.location?.origin ?? 'http://localhost';
	}

	reload(): void {
		globalThis.location?.reload();
	}

	emitExternalNavigation(
		url: string,
		options?: ExternalNavigationOptions
	): void {
		const target = options?.target;

		if (target) {
			globalThis.open?.(url, target, options.features);
			return;
		}

		if (options?.replace) {
			globalThis.location?.replace(url);
			return;
		}

		if (globalThis.location) {
			globalThis.location.href = url;
		}
	}

	openExternalTab(): ExternalTab | null {
		const openedTab = globalThis.open?.('', '_blank') ?? null;

		if (!openedTab) {
			return null;
		}

		openedTab.opener = null;

		return {
			close: () => openedTab.close(),
			navigate: (url: string) => {
				openedTab.location.href = url;
			}
		};
	}

	createScopedRouteParams(args: {
		organizationIdn: string;
		organizationType: string | null;
	}): RouteParams {
		if (args.organizationType === OrganizationType.Partner) {
			return {
				organizationIdn: args.organizationIdn,
				organizationType: OrganizationType.Partner
			};
		}

		if (args.organizationType === OrganizationType.Customer) {
			return {
				organizationIdn: args.organizationIdn,
				organizationType: OrganizationType.Customer
			};
		}

		throw new Error(
			`Unknown organization route type: ${args.organizationType}`
		);
	}

	isCustomerScoped(): boolean {
		return this.getRouteParams().organizationType === OrganizationType.Customer;
	}

	isPartnerScoped(): boolean {
		return this.getRouteParams().organizationType === OrganizationType.Partner;
	}

	#subscribeToRouterLocation = (listener: () => void): (() => void) => {
		const handleLocationChange = () => {
			this.#resolveCurrentRoute();
			listener();
		};

		globalThis.addEventListener?.('popstate', handleLocationChange);
		globalThis.addEventListener?.(
			ROUTING_CLIENT_LOCATION_CHANGE_EVENT,
			handleLocationChange
		);

		return () => {
			globalThis.removeEventListener?.('popstate', handleLocationChange);
			globalThis.removeEventListener?.(
				ROUTING_CLIENT_LOCATION_CHANGE_EVENT,
				handleLocationChange
			);
		};
	};

	#getRouterLocationSnapshot = (): RouterLocation => {
		const nextLocation = getRouterLocation();
		const currentLocation = this.#routerLocation;

		if (
			currentLocation.pathname === nextLocation.pathname &&
			currentLocation.search === nextLocation.search &&
			currentLocation.hash === nextLocation.hash &&
			Object.is(currentLocation.state, nextLocation.state)
		) {
			return currentLocation;
		}

		this.#routerLocation = nextLocation;
		return nextLocation;
	};

	useCurrentRoute(): ResolvedRoute | null {
		this.#assertBootstrapped();

		const { pathname, search } = useLocation();

		return useMemo(
			() => this.#registry.match(`${pathname}${search}`),
			[pathname, search]
		);
	}

	Provider: FC<RouterProps> = ({ children }) => {
		const location = useSyncExternalStore(
			this.#subscribeToRouterLocation,
			this.#getRouterLocationSnapshot,
			this.#getRouterLocationSnapshot
		);
		const navigator = useMemo(
			() => ({
				createHref: createRouterHref,
				go: (delta: number) => globalThis.history?.go(delta),
				push: (to: RouterTo, state?: unknown) => {
					globalThis.history?.pushState(state, '', createRouterHref(to));
					globalThis.dispatchEvent(
						new Event(ROUTING_CLIENT_LOCATION_CHANGE_EVENT)
					);
				},
				replace: (to: RouterTo, state?: unknown) => {
					globalThis.history?.replaceState(state, '', createRouterHref(to));
					globalThis.dispatchEvent(
						new Event(ROUTING_CLIENT_LOCATION_CHANGE_EVENT)
					);
				}
			}),
			[]
		);

		return (
			<Router
				location={location}
				navigator={navigator}
			>
				{children}
			</Router>
		);
	};

	Router: FC<RouterProps> = ({ children }) => (
		<Routes>{this.#createRouteElements(children)}</Routes>
	);

	Route: FC<RouteElementProps> = ({ element, intent }) => {
		return this.#createIntentRouteElements({ element, intent });
	};

	NotFound: FC<RouteContentProps> = ({ element }) => (
		<Route
			element={element}
			path="*"
		/>
	);

	RouteAccessGate = createRouteAccessGate((request) => {
		this.emitNavigation(request);
	});

	#createRouteElements(children: ReactNode): ReactNode {
		return Children.map(children, (child) => {
			if (!isValidElement(child)) {
				return child;
			}

			if (child.type === Fragment) {
				return (
					<Fragment>
						{this.#createRouteElements(
							(child.props as { children?: ReactNode }).children
						)}
					</Fragment>
				);
			}

			if (child.type === this.Route) {
				return this.#createIntentRouteElements(
					child.props as RouteElementProps
				);
			}

			if (child.type === this.NotFound) {
				return (
					<Route
						element={(child.props as RouteContentProps).element}
						path="*"
					/>
				);
			}

			return child;
		});
	}

	#createIntentRouteElements({
		element,
		intent
	}: RouteElementProps): ReactNode {
		this.#assertBootstrapped();

		const route = this.#registry.getRoute(intent);

		if (
			!route ||
			route.matchPaths === undefined ||
			route.matchPaths.length === 0
		) {
			throw new Error(`Route rendering is unavailable for intent: ${intent}`);
		}

		if (!this.#isAppAvailable(route.appId)) {
			throw new Error(
				`App is not available for route rendering: ${route.appId}`
			);
		}

		return (
			<Fragment>
				{route.matchPaths.map((matchPath) => (
					<Route
						element={element}
						key={matchPath}
						path={matchPath}
					/>
				))}
			</Fragment>
		);
	}

	#assertBootstrapped(): void {
		if (!this.#isBootstrapped) {
			throw new Error('routingClient must be bootstrapped before navigation');
		}
	}

	#isAppAvailable(appId: AppId): boolean {
		return this.#registeredAppIds.includes(appId);
	}

	#isRouteReachable(route: ResolvedRoute): boolean {
		return (
			this.#isAppAvailable(route.appId) ||
			Boolean(this.#externalAppOrigins[route.appId])
		);
	}

	#resolveExternalUrl(route: ResolvedRoute): string | null {
		const origin = this.#externalAppOrigins[route.appId];

		if (!origin) {
			return null;
		}

		return new URL(route.url, origin).toString();
	}

	#withCurrentRouteParams(request: NavigationRequest): NavigationRequest {
		if (request.params?.organizationIdn || request.params?.organizationType) {
			return request;
		}

		const currentParams = this.#resolveCurrentRoute()?.params;

		if (!currentParams?.organizationIdn || !currentParams.organizationType) {
			return request;
		}

		return {
			...request,
			params: {
				...request.params,
				organizationIdn: currentParams.organizationIdn,
				organizationType: currentParams.organizationType
			}
		};
	}

	#navigate(url: string, request: NavigationRequest): void {
		if (!globalThis.history) {
			return;
		}

		if (request.replace) {
			globalThis.history.replaceState(null, '', url);
		} else {
			globalThis.history.pushState(null, '', url);
		}

		globalThis.dispatchEvent(new Event(ROUTING_CLIENT_LOCATION_CHANGE_EVENT));
	}

	#resolveCurrentRoute(): ResolvedRoute | null {
		const currentPath = this.#getCurrentPath();

		if (!currentPath) {
			return this.#currentRoute;
		}

		const matchedRoute = this.#registry.match(currentPath);

		if (
			this.#currentRoute &&
			matchedRoute &&
			this.#currentRoute.url === matchedRoute.url &&
			this.#currentRoute.intent === matchedRoute.intent &&
			this.#currentRoute.appId === matchedRoute.appId
		) {
			return this.#currentRoute;
		}

		this.#currentRoute = matchedRoute;
		return this.#currentRoute;
	}

	#getCurrentPath(): string {
		if (!globalThis.location) {
			return '';
		}

		return `${globalThis.location.pathname}${globalThis.location.search}`;
	}

	#matchScopedPrefix(
		url: URL
	): { organizationType: OrganizationType; organizationIdn: string } | null {
		const pathParts = url.pathname.split('/').filter(Boolean);
		const [organizationType, organizationIdn] = pathParts;

		if (!organizationIdn) {
			return null;
		}

		if (
			organizationType !== OrganizationType.Customer &&
			organizationType !== OrganizationType.Partner
		) {
			return null;
		}

		return {
			organizationType,
			organizationIdn: decodeURIComponent(organizationIdn)
		};
	}

	#parseSafeUrl(url: string): URL | null {
		const currentOrigin = this.getOrigin();

		if (url.startsWith('/')) {
			return new URL(url, currentOrigin);
		}

		try {
			const parsedUrl = new URL(url);

			if (parsedUrl.origin !== currentOrigin) {
				return null;
			}

			return parsedUrl;
		} catch {
			return null;
		}
	}
}

export const routingClient = new RoutingClient();
